import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { renderTemplate } from '../utils/templateRenderer';
import { sanitizeTemplateHTML } from '../utils/templateSanitizer';
import { loadCoverLetterTemplates, loadCoverLetterTemplateHTML } from '../services/templateService';
import { ServerExportService } from '../services/serverExportService';
import { auth, db } from '../firebase/firebaseConfig';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { customToast } from '../utils/toastUtils';
import { useUser } from '../contexts/UserContext';
import FeedbackModal from '../components/FeedbackModal';
import PhotoUploadModal from '../components/PhotoUploadModal';
import { getTierColorPolicy } from '../utils/colorThemeEngine';
import { saveCoverLetterEditorState, restoreCoverLetterEditorState, clearCoverLetterEditorState } from '../utils/editorStatePreserver';
import { buildCoverLetterTemplateData } from '../utils/coverLetterDataMapper';

import './CoverLetterEditor.css';

const CoverLetterEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState(location.state?.selectedTemplate || null);
  const { userData } = useUser();

  // Pre-fill form data if passed via navigation state (e.g. from test page or import flow)
  const incomingFormData = location.state?.prefillFormData || null;

  const restoredFromSessionRef = useRef(false);

  // Mobile view toggle: 'editor' or 'preview'
  const [mobileView, setMobileView] = useState('editor');

  const [loading, setLoading] = useState(true);
  const [templateHTML, setTemplateHTML] = useState('');
  const [previewHTML, setPreviewHTML] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Photo upload state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');
  
  // Track iframe content height so we can size the wrapper to show ALL content
  const [clIframeHeight, setClIframeHeight] = useState(1123); // Default = 1 A4 page
  const clIframeRef = useRef(null);

  // Preview zoom state
  const [clPreviewZoom, setClPreviewZoom] = useState(0.5);
  const handleClZoomIn  = () => setClPreviewZoom(z => Math.min(+(z + 0.1).toFixed(1), 1.5));
  const handleClZoomOut = () => setClPreviewZoom(z => Math.max(+(z - 0.1).toFixed(1), 0.3));
  const handleClZoomFit = () => setClPreviewZoom(0.5);

  // Listen for height reports from the cover letter preview iframe
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === 'cl-height') {
        setClIframeHeight(e.data.height || 1123);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Theme picker state
  const [colorTheme, setColorTheme] = useState('blue');
  const [fontFamily, setFontFamily] = useState('Inter');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const colorBtnRef = useRef(null);
  const fontBtnRef = useRef(null);
  const [fontFocusIdx, setFontFocusIdx] = useState(-1);
  const fontListRef = useRef(null);

  // Color palette configuration
  const colorPalettes = {
    teal: { primary: '#0d9488', dark: '#115e59', light: '#5eead4', bg: '#ccfbf1', gradient: ['#0f766e', '#14b8a6'] },
    blue: { primary: '#2563eb', dark: '#1e40af', light: '#60a5fa', bg: '#dbeafe', gradient: ['#1d4ed8', '#3b82f6'] },
    purple: { primary: '#9333ea', dark: '#7e22ce', light: '#c084fc', bg: '#f3e8ff', gradient: ['#7c3aed', '#a855f7'] },
    indigo: { primary: '#4f46e5', dark: '#3730a3', light: '#818cf8', bg: '#e0e7ff', gradient: ['#4338ca', '#6366f1'] },
    rose: { primary: '#e11d48', dark: '#be123c', light: '#fb7185', bg: '#ffe4e6', gradient: ['#be123c', '#f43f5e'] },
    emerald: { primary: '#059669', dark: '#047857', light: '#34d399', bg: '#d1fae5', gradient: ['#047857', '#10b981'] },
    cyan: { primary: '#06b6d4', dark: '#0e7490', light: '#67e8f9', bg: '#cffafe', gradient: ['#0891b2', '#22d3ee'] },
    amber: { primary: '#f59e0b', dark: '#d97706', light: '#fbbf24', bg: '#fef3c7', gradient: ['#d97706', '#f59e0b'] },
    orange: { primary: '#f97316', dark: '#ea580c', light: '#fb923c', bg: '#ffedd5', gradient: ['#ea580c', '#f97316'] },
    pink: { primary: '#ec4899', dark: '#db2777', light: '#f472b6', bg: '#fce7f3', gradient: ['#db2777', '#ec4899'] },
    slate: { primary: '#475569', dark: '#334155', light: '#94a3b8', bg: '#e2e8f0', gradient: ['#334155', '#475569'] }
  };

  // Font family options
  const fontFamilies = [
    { value: 'Inter', label: 'Inter' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Raleway', label: 'Raleway' },
    { value: 'Playfair Display', label: 'Playfair Display' },
    { value: 'Merriweather', label: 'Merriweather' },
    { value: 'Source Sans Pro', label: 'Source Sans Pro' }
  ];

  // Form data state — pre-fill from navigation state if available (test page / import flow)
  const [formData, setFormData] = useState(() => {
    const defaults = {
      // Personal Information
      fullName: '',
      email: '',
      phone: '',
      location: '',
      linkedin: '',
      title: '',
      
      // Letter Meta
      currentDate: new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      hiringManagerName: '',
      hiringManagerSalutation: 'Hiring Manager',
      companyName: '',
      companyLocation: '',
      
      // Job Details
      jobTitle: '',
      yearsOfExperience: '',
      primarySkills: '',
      
      // Current Position
      currentRole: '',
      currentCompany: '',
      keyAchievementOne: '',
      keyAchievementTwo: '',
      
      // Additional Details
      toolsAndTechnologies: '',
      companyMotivation: '',
      relevantExperience: ''
    };
    // Merge incoming pre-fill data over defaults
    if (incomingFormData) {
      return { ...defaults, ...incomingFormData };
    }
    return defaults;
  });

  useEffect(() => {
    if (!selectedTemplate) {
      // Check if returning from pricing — restore saved editor state
      const savedState = restoreCoverLetterEditorState();
      if (savedState?.selectedTemplate) {
        restoredFromSessionRef.current = true;
        setSelectedTemplate(savedState.selectedTemplate);
        if (savedState.formData) setFormData(prev => ({ ...prev, ...savedState.formData }));
        if (savedState.templateHTML) setTemplateHTML(savedState.templateHTML);
        if (savedState.colorTheme) setColorTheme(savedState.colorTheme);
        if (savedState.fontFamily) setFontFamily(savedState.fontFamily);
        setLoading(false);
        clearCoverLetterEditorState();
        return; // Don't redirect — state is restored
      }
      navigate('/cover-letter-templates');
      return;
    }

    // Auto-trigger download dialog when returning from pricing page after payment
    if (location.state?.triggerDownload) {
      const message = location.state?.message;
      if (message) {
        customToast.success(message, { autoClose: 8000 });
      }
      if (location.state?.draftSavedToMyFiles) {
        setTimeout(() => {
          customToast.info('📁 Your cover letter editing progress has been preserved. You can now download it!', { autoClose: 10000 });
        }, 2000);
      }
      const timer = setTimeout(() => {
        setShowExportDialog(true);
      }, 1500);
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }

    // Skip template fetch if restored from session (already have templateHTML)
    if (restoredFromSessionRef.current) {
      restoredFromSessionRef.current = false;
      return;
    }

    const fetchTemplate = async () => {
      try {
        setLoading(true);
        let html;
        
        // Use the centralized template service to load HTML
        // It handles local vs Firebase based on VITE_USE_LOCAL_COVER_TEMPLATES
        const storagePath = selectedTemplate.storagePath || 
          `templates/cover-letter/${selectedTemplate.plan}/${selectedTemplate.filename}`;
        html = await loadCoverLetterTemplateHTML(storagePath);
        
        const sanitized = sanitizeTemplateHTML(html);
        setTemplateHTML(sanitized);
        
        // Initial render with empty data and default theme
        updatePreview(sanitized, formData, colorTheme, fontFamily);
        
      } catch (error) {
        console.error('❌ Error loading cover letter template:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [selectedTemplate, navigate]);

  // Update preview when form data changes
  const updatePreview = (html, data, theme = colorTheme, font = fontFamily) => {
    try {
      // Build an enriched data object that includes BOTH the form field names
      // AND legacy template placeholder aliases so ALL 24 templates render properly.
      const enriched = buildCoverLetterTemplateData(data);
      const rendered = renderTemplate(html, enriched);
      const styledHTML = applyThemeStyles(rendered, theme, font);
      
      // Inject a small script to report the actual content height to the parent
      const heightScript = `
        <script>
          (function(){
            function report(){
              var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
              window.parent.postMessage({type:'cl-height', height: h},'*');
            }
            // Report after fonts and images load
            Promise.all([
              document.fonts ? document.fonts.ready : Promise.resolve(),
              new Promise(function(r){ window.addEventListener('load', r); })
            ]).then(function(){
              setTimeout(report, 200);
              setTimeout(report, 800);
            });
            // Also report on resize and DOM changes
            window.addEventListener('resize', report);
            if(window.MutationObserver){
              new MutationObserver(function(){ setTimeout(report, 100); })
                .observe(document.documentElement, {childList:true, subtree:true, attributes:true, characterData:true});
            }
          })();
        <\/script>
      `;
      
      // Inject before </body> or append
      let finalHTML = styledHTML;
      if (finalHTML.includes('</body>')) {
        finalHTML = finalHTML.replace('</body>', `${heightScript}</body>`);
      } else {
        finalHTML = finalHTML + heightScript;
      }
      
      setPreviewHTML(finalHTML);
    } catch (error) {
      console.error('❌ Error rendering preview:', error);
    }
  };

  // Apply dynamic color and font styles
  // ONLY injects CSS custom properties + font — templates use var() references
  // for accent elements. Body text / gray backgrounds are NEVER overridden.
  const applyThemeStyles = (html, theme, font) => {
    const palette = colorPalettes[theme];
    
    const styleTag = `
      <style id="dynamic-theme-styles">
        @import url('https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}:wght@300;400;500;600;700;800&display=swap');
        
        :root {
          --primary-color: ${palette.primary};
          --primary-dark: ${palette.dark};
          --primary-light: ${palette.light};
          --primary-bg: ${palette.bg};
          --gradient-from: ${palette.gradient[0]};
          --gradient-to: ${palette.gradient[1]};
          --font-family: '${font}', sans-serif;
        }

        body { font-family: var(--font-family) !important; }
      </style>
    `;
    
    // Insert style tag before closing </head> or at the beginning of <body>
    if (html.includes('</head>')) {
      return html.replace('</head>', `${styleTag}</head>`);
    } else if (html.includes('<body>')) {
      return html.replace('<body>', `<body>${styleTag}`);
    } else {
      return `${styleTag}${html}`;
    }
  };

  const handleInputChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    updatePreview(templateHTML, newData, colorTheme, fontFamily);
  };

  // Check if template supports photo upload (platinum templates with photo tokens)
  const supportsPhoto = () => {
    try {
      const tier = (selectedTemplate?.plan || selectedTemplate?.tier || '').toLowerCase();
      const templateId = (selectedTemplate?.id || selectedTemplate?.name || '').toLowerCase();
      if (tier === 'platinum' || templateId.includes('platinum')) return true;
      if (templateHTML) {
        const photoTokens = ['photoURL', 'photo', 'profilePhotoUrl', 'avatar', 'headshot'];
        return photoTokens.some(token =>
          templateHTML.includes(`{{${token}}}`) || templateHTML.includes(`{{#${token}}}`)
        );
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  const handlePhotoUpload = (croppedImage) => {
    setPhotoPreview(croppedImage.dataUrl);
    setFormData(prev => {
      const newData = { ...prev, photoURL: croppedImage.dataUrl, photoDataUri: croppedImage.dataUrl };
      updatePreview(templateHTML, newData, colorTheme, fontFamily);
      return newData;
    });
  };

  // Tier-based color policy
  const templateTier = (selectedTemplate?.plan || selectedTemplate?.tier || '').toLowerCase();
  const userPlan = (userData?.planType || 'free').toLowerCase();
  const templatePolicy = getTierColorPolicy(templateTier);
  const tierColorPolicy = (userPlan === 'free' || userPlan === 'silver')
    ? { enabled: false, label: 'Color themes require Gold or Platinum plan. Upgrade to customize colors.' }
    : templatePolicy;

  const handleColorChange = (newColor) => {
    setColorTheme(newColor);
    setShowColorPicker(false);
    updatePreview(templateHTML, formData, newColor, fontFamily);
  };

  const handleFontChange = (newFont) => {
    setFontFamily(newFont);
    setShowFontPicker(false);
    updatePreview(templateHTML, formData, colorTheme, newFont);
  };

  // Theme picker toggle handlers (positioned dropdown below button)
  const handleColorPickerToggle = () => {
    if (colorBtnRef.current) {
      const rect = colorBtnRef.current.getBoundingClientRect();
      const dropdownWidth = 300;
      const dropdownHeight = 400;
      let top = rect.bottom + 8;
      let left = rect.left;
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 20;
      }
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 8;
      }
      setDropdownPosition({ top, left });
    }
    setShowColorPicker(!showColorPicker);
    setShowFontPicker(false);
  };

  // Keyboard navigation for font picker
  const handleFontKeyDown = (e) => {
    const len = fontFamilies.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFontFocusIdx(i => Math.min(i + 1, len - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFontFocusIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setShowFontPicker(false);
    }
  };

  // Focus the highlighted font button whenever fontFocusIdx changes
  useEffect(() => {
    if (showFontPicker && fontFocusIdx >= 0 && fontListRef.current) {
      const btns = fontListRef.current.querySelectorAll('button');
      if (btns[fontFocusIdx]) btns[fontFocusIdx].focus();
    }
  }, [fontFocusIdx, showFontPicker]);

  // When font picker opens, focus the currently selected font
  useEffect(() => {
    if (showFontPicker) {
      const idx = fontFamilies.findIndex(f => f.value === fontFamily);
      setFontFocusIdx(idx >= 0 ? idx : 0);
    } else {
      setFontFocusIdx(-1);
    }
  }, [showFontPicker]);

  const handleFontPickerToggle = () => {
    if (fontBtnRef.current) {
      const rect = fontBtnRef.current.getBoundingClientRect();
      const dropdownWidth = 300;
      const dropdownHeight = 450;
      let top = rect.bottom + 8;
      let left = rect.left;
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 20;
      }
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 8;
      }
      setDropdownPosition({ top, left });
    }
    setShowFontPicker(!showFontPicker);
    setShowColorPicker(false);
  };

  const handleBackToTemplates = () => {
    navigate('/cover-letter-templates');
  };

  /**
   * Compile Tailwind CSS from the preview HTML using a hidden iframe.
   * The iframe loads the HTML (with Tailwind CDN), waits for compilation,
   * extracts the generated <style> output, and returns self-contained HTML.
   */
  const compileTailwindForExport = useCallback((htmlString) => {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:794px;height:1123px;visibility:hidden;pointer-events:none;';
      // Need allow-same-origin to read iframe stylesheets after compilation
      // Security note: this iframe only loads our own template HTML, not user content
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
      document.body.appendChild(iframe);

      const timeout = setTimeout(() => {
        cleanup();
        resolve(htmlString);
      }, 10000);

      const cleanup = () => {
        clearTimeout(timeout);
        try { document.body.removeChild(iframe); } catch (e) { /* already removed */ }
      };

      iframe.onload = () => {
        // Wait for Tailwind to process and generate styles
        const waitForTailwind = () => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            
            // Collect ALL CSS from the iframe (Tailwind-generated + template styles)
            let allCSS = '';
            const sheets = iframeDoc.styleSheets;
            for (let i = 0; i < sheets.length; i++) {
              try {
                const rules = sheets[i].cssRules || sheets[i].rules;
                if (rules) {
                  for (let j = 0; j < rules.length; j++) {
                    allCSS += rules[j].cssText + '\n';
                  }
                }
              } catch (e) {
                // Cross-origin stylesheet — skip (CDN link stylesheets)
                // Try to get href and re-include as <link>
                if (sheets[i].href) {
                  // We'll handle external fonts separately
                }
              }
            }

            // Also collect any inline <style> content that wasn't in styleSheets
            const inlineStyles = iframeDoc.querySelectorAll('style');
            inlineStyles.forEach(style => {
              if (!allCSS.includes(style.textContent.trim().substring(0, 50))) {
                allCSS += style.textContent + '\n';
              }
            });

            if (allCSS.length < 500) {
              // Tailwind hasn't compiled yet — retry
              setTimeout(waitForTailwind, 200);
              return;
            }

            // Build clean export HTML: strip ALL <script> tags, inject compiled CSS
            let exportDoc = iframeDoc.documentElement.outerHTML;
            
            // Remove all script tags (Tailwind CDN, config, etc.)
            exportDoc = exportDoc.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
            
            // Inject compiled CSS into <head>
            const compiledStyleTag = `<style id="tailwind-compiled">\n${allCSS}\n</style>`;
            if (exportDoc.includes('</head>')) {
              exportDoc = exportDoc.replace('</head>', `${compiledStyleTag}\n</head>`);
            } else {
              exportDoc = compiledStyleTag + exportDoc;
            }

            // Wrap in proper doctype
            exportDoc = `<!DOCTYPE html>\n<html lang="en">\n${exportDoc}\n</html>`;

            cleanup();
            resolve(exportDoc);
          } catch (err) {
            console.error('Error extracting styles from iframe:', err);
            cleanup();
            resolve(htmlString); // Fallback to original
          }
        };

        // Give Tailwind CDN time to load and compile (usually ~1-2s)
        setTimeout(waitForTailwind, 2000);
      };

      iframe.onerror = () => {
        cleanup();
        resolve(htmlString); // Fallback
      };

      iframe.srcdoc = htmlString;
    });
  }, []);

  const handleDownloadPDF = async () => {
    // Helper: save cover letter state to sessionStorage then redirect to pricing
    // After payment, user returns to the same editor with state restored
    const saveStateThenNavigate = (pricingState = {}) => {
      saveCoverLetterEditorState({
        formData,
        templateHTML,
        selectedTemplate,
        colorTheme,
        fontFamily,
      });
      navigate('/pricing', {
        state: {
          ...pricingState,
          returnUrl: '/cover-letter-editor',
          returnAction: pricingState.returnAction || 'download-cover-letter',
          draftSavedToMyFiles: true
        }
      });
    };

    try {
      // Mandatory fields validation — only essential fields required
      const requiredFields = [
        { value: formData.fullName, label: 'Full Name' },
        { value: formData.email, label: 'Email' },
        { value: formData.phone, label: 'Phone' },
        { value: formData.companyName, label: 'Company Name' },
        { value: formData.jobTitle, label: 'Job Title' },
      ];
      const emptyFields = requiredFields.filter(f => !f.value || !f.value.trim());
      if (emptyFields.length > 0) {
        customToast.warning(`Please fill the mandatory fields: ${emptyFields.map(f => f.label).join(', ')}`);
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        customToast.warning('Please sign in to download your cover letter');
        navigate('/profile');
        return;
      }

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userDataSnap = userSnap.exists() ? userSnap.data() : { planType: 'free' };

      const plan = (userDataSnap.planType || 'free').toLowerCase();
      if (plan === 'free') {
        saveStateThenNavigate({
          message: 'Upgrade to download cover letters and access premium features',
          returnAction: 'download-cover-letter'
        });
        return;
      }

      // Check plan expiry
      if (userDataSnap.planExpiryDate) {
        const expiryDate = userDataSnap.planExpiryDate.toDate ? userDataSnap.planExpiryDate.toDate() : new Date(userDataSnap.planExpiryDate);
        const now = new Date();
        if (expiryDate < now) {
          const expiredDays = Math.floor((now - expiryDate) / (1000 * 60 * 60 * 24));
          customToast.error(`Your ${plan.toUpperCase()} plan expired ${expiredDays} day(s) ago. Please upgrade.`);
          saveStateThenNavigate({
            message: 'Your plan has expired. Renew to continue downloading.',
            returnAction: 'download-cover-letter'
          });
          return;
        }
      }

      // Check cover letter download limits
      const clLimit = userDataSnap.coverLetterDownloadLimit ?? userDataSnap.resumeDownloadLimit ?? 0;
      const clUsed = userDataSnap.coverLetterDownloadsUsed ?? 0;
      const isUnlimited = clLimit === -1;

      if (!isUnlimited && clUsed >= clLimit) {
        customToast.error(`Cover letter download limit reached (${clUsed}/${clLimit}). Please upgrade your plan.`);
        return;
      }

      if (!previewHTML) {
        customToast.warning('Please fill in some details before downloading.');
        return;
      }

      setDownloading(true);
      setShowExportDialog(true);
      setExportProgress(10);
      setExportStatus('Preparing cover letter...');

      // Step 1: Compile Tailwind CSS from preview HTML
      // The preview HTML contains Tailwind CDN <script> tags which the backend
      // strips for security. We compile Tailwind in a hidden iframe first,
      // extract the generated CSS, and produce self-contained HTML.
      setExportProgress(30);
      setExportStatus('Compiling styles...');
      
      let exportHTML = await compileTailwindForExport(previewHTML);

      setExportProgress(60);
      setExportStatus('Generating PDF...');

      // Step 2: Inject export-specific CSS for A4 page sizing
      const exportCSS = `
        <style id="export-match-preview">
          @page :first {
            size: A4;
            margin: 0mm;
          }
          @page {
            size: A4;
            margin: 14mm 0mm 8mm 0mm;
          }
          html {
            width: auto !important;
            height: auto !important;
            overflow: visible !important;
          }
          body {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
        </style>
      `;
      if (exportHTML.includes('</head>')) {
        exportHTML = exportHTML.replace('</head>', `${exportCSS}</head>`);
      } else if (exportHTML.includes('<body>')) {
        exportHTML = exportHTML.replace('<body>', `<body>${exportCSS}`);
      } else {
        exportHTML = `${exportCSS}${exportHTML}`;
      }

      const fileName = `Cover_Letter_${formData.fullName || 'Untitled'}_${formData.companyName || 'Company'}.pdf`
        .replace(/[^a-zA-Z0-9_.-]/g, '_');

      const result = await ServerExportService.generatePDF(exportHTML, fileName, user.uid);

      if (!result.success) {
        throw new Error(result.error || 'PDF generation failed');
      }

      setExportProgress(90);
      setExportStatus('Updating records...');

      // Update cover letter download count
      await updateDoc(userRef, {
        coverLetterDownloadsUsed: (userDataSnap.coverLetterDownloadsUsed || 0) + 1,
        lastDownloadAt: serverTimestamp(),
        coverLetterDownloadCount: (userDataSnap.coverLetterDownloadCount || 0) + 1
      });

      setExportProgress(100);
      setExportStatus('Complete!');
      customToast.success('Cover letter downloaded successfully!');

      setTimeout(() => {
        setShowExportDialog(false);
        setExportProgress(0);
        setExportStatus('');
        setDownloading(false);
        setShowFeedbackModal(true);
      }, 1000);

    } catch (error) {
      console.error('Cover letter export error:', error);
      customToast.error(`Download failed: ${error.message}`);
      setShowExportDialog(false);
      setExportProgress(0);
      setExportStatus('');
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="cl-editor-loading">
        <div className="cl-spinner"></div>
        <p>Loading Cover Letter Editor...</p>
      </div>
    );
  }

  return (
    <div className={`cl-editor-container ${mobileView === 'preview' ? 'cl-mobile-preview-active' : ''}`}>
      {/* Mobile Section Navigation Bar */}
      <div className="cl-mobile-sections-bar">
        <div className="cl-mobile-sections-scroll">
          <button className="cl-mobile-section-pill active" onClick={() => { setMobileView('editor'); document.querySelector('.cl-form-scroll')?.scrollTo(0, 0); }}>
            <span>👤</span> Personal
          </button>
          <button className="cl-mobile-section-pill" onClick={() => { setMobileView('editor'); document.querySelector('[class*="cl-form-section"]:nth-child(2)')?.scrollIntoView({ behavior: 'smooth' }); }}>
            <span>🏢</span> Company
          </button>
          <button className="cl-mobile-section-pill" onClick={() => { setMobileView('editor'); document.querySelector('[class*="cl-form-section"]:nth-child(3)')?.scrollIntoView({ behavior: 'smooth' }); }}>
            <span>💼</span> Experience
          </button>
          <button className="cl-mobile-section-pill" onClick={() => { setMobileView('editor'); document.querySelector('[class*="cl-form-section"]:nth-child(4)')?.scrollIntoView({ behavior: 'smooth' }); }}>
            <span>❤️</span> Motivation
          </button>
        </div>
      </div>

      {/* Main Two-Column Layout */}
      <div className="cl-editor-main">
        {/* Left: Scrollable Form */}
        <div className="cl-editor-form">
          {/* Form Header */}
          <div className="cl-form-header">
            <div className="cl-form-header-left">
              <button className="cl-back-btn" onClick={handleBackToTemplates}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back
              </button>
              <div className="cl-form-header-info">
                <h1 className="cl-editor-title">Cover Letter Editor</h1>
                <span className="cl-editor-subtitle">{selectedTemplate?.name}</span>
              </div>
            </div>
          </div>

          {/* Form Sections */}
          <div className="cl-form-scroll">
            {/* Personal Information */}
            <div className="cl-form-section">
              <h2 className="cl-section-title">
                <svg viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Personal Information
              </h2>
              
              <div className="cl-form-group">
                <label>Full Name *</label>
                <input type="text" value={formData.fullName} onChange={(e) => handleInputChange('fullName', e.target.value)} placeholder="John Doe" />
              </div>

              <div className="cl-form-row">
                <div className="cl-form-group">
                  <label>Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="john.doe@email.com" />
                </div>
                <div className="cl-form-group">
                  <label>Phone *</label>
                  <input type="tel" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} placeholder="+1 (555) 123-4567" />
                </div>
              </div>

              <div className="cl-form-row">
                <div className="cl-form-group">
                  <label>Location</label>
                  <input type="text" value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} placeholder="San Francisco, CA" />
                </div>
                <div className="cl-form-group">
                  <label>LinkedIn Profile</label>
                  <input type="url" value={formData.linkedin} onChange={(e) => handleInputChange('linkedin', e.target.value)} placeholder="linkedin.com/in/johndoe" />
                </div>
              </div>

              <div className="cl-form-group">
                <label>Professional Title</label>
                <input type="text" value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)} placeholder="Senior Software Engineer" />
              </div>

              {/* Photo Upload — visible only for platinum templates */}
              {supportsPhoto() && (
                <div style={{
                  marginTop: '12px',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                  borderRadius: '10px',
                  border: '1px solid #bae6fd'
                }}>
                  <label style={{ marginBottom: '8px', display: 'block', fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>
                    📷 Profile Photo <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Platinum feature)</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    {(photoPreview || formData.photoURL) && (
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '3px solid #3b82f6',
                        boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                        flexShrink: 0
                      }}>
                        <img
                          src={photoPreview || formData.photoURL}
                          alt="Profile"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => setShowPhotoModal(true)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '7px 14px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '7px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#2563eb'}
                        onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                          <circle cx="12" cy="13" r="4"></circle>
                        </svg>
                        {(photoPreview || formData.photoURL) ? 'Change Photo' : 'Upload Photo'}
                      </button>
                      {(photoPreview || formData.photoURL) && (
                        <button
                          type="button"
                          onClick={() => {
                            setPhotoPreview('');
                            const newData = { ...formData, photoURL: '', photoDataUri: '' };
                            setFormData(newData);
                            updatePreview(templateHTML, newData, colorTheme, fontFamily);
                          }}
                          style={{
                            padding: '5px 10px',
                            background: 'transparent',
                            color: '#ef4444',
                            border: '1px solid #fca5a5',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 500
                          }}
                        >
                          🗑️ Remove
                        </button>
                      )}
                      <small style={{ color: '#64748b', fontSize: '10px' }}>Square format recommended, max 2MB</small>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Company & Position Details */}
            <div className="cl-form-section">
              <h2 className="cl-section-title">
                <svg viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Company & Position Details
              </h2>
              
              <div className="cl-form-row">
                <div className="cl-form-group">
                  <label>Company Name *</label>
                  <input type="text" value={formData.companyName} onChange={(e) => handleInputChange('companyName', e.target.value)} placeholder="Google Inc." />
                </div>
                <div className="cl-form-group">
                  <label>Company Location</label>
                  <input type="text" value={formData.companyLocation} onChange={(e) => handleInputChange('companyLocation', e.target.value)} placeholder="Mountain View, CA" />
                </div>
              </div>

              <div className="cl-form-group">
                <label>Job Title *</label>
                <input type="text" value={formData.jobTitle} onChange={(e) => handleInputChange('jobTitle', e.target.value)} placeholder="Senior Frontend Developer" />
              </div>

              <div className="cl-form-row">
                <div className="cl-form-group">
                  <label>Hiring Manager Name</label>
                  <input type="text" value={formData.hiringManagerName} onChange={(e) => handleInputChange('hiringManagerName', e.target.value)} placeholder="Jane Smith" />
                </div>
                <div className="cl-form-group">
                  <label>Salutation</label>
                  <input type="text" value={formData.hiringManagerSalutation} onChange={(e) => handleInputChange('hiringManagerSalutation', e.target.value)} placeholder="Hiring Manager" />
                </div>
              </div>
            </div>

            {/* Your Experience */}
            <div className="cl-form-section">
              <h2 className="cl-section-title">
                <svg viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Your Experience
              </h2>
              
              <div className="cl-form-row">
                <div className="cl-form-group">
                  <label>Years of Experience</label>
                  <input type="text" value={formData.yearsOfExperience} onChange={(e) => handleInputChange('yearsOfExperience', e.target.value)} placeholder="5" />
                </div>
                <div className="cl-form-group">
                  <label>Primary Skills</label>
                  <input type="text" value={formData.primarySkills} onChange={(e) => handleInputChange('primarySkills', e.target.value)} placeholder="React, Node.js, TypeScript" />
                </div>
              </div>

              <div className="cl-form-row">
                <div className="cl-form-group">
                  <label>Current Role</label>
                  <input type="text" value={formData.currentRole} onChange={(e) => handleInputChange('currentRole', e.target.value)} placeholder="Frontend Developer" />
                </div>
                <div className="cl-form-group">
                  <label>Current Company</label>
                  <input type="text" value={formData.currentCompany} onChange={(e) => handleInputChange('currentCompany', e.target.value)} placeholder="ABC Tech Solutions" />
                </div>
              </div>

              <div className="cl-form-group">
                <label>Key Achievement #1</label>
                <textarea value={formData.keyAchievementOne} onChange={(e) => handleInputChange('keyAchievementOne', e.target.value)} rows="2" placeholder="Led development of a feature that increased user engagement by 30%" />
              </div>

              <div className="cl-form-group">
                <label>Key Achievement #2</label>
                <textarea value={formData.keyAchievementTwo} onChange={(e) => handleInputChange('keyAchievementTwo', e.target.value)} rows="2" placeholder="Optimized application performance, reducing load times by 40%" />
              </div>

              <div className="cl-form-group">
                <label>Tools & Technologies</label>
                <input type="text" value={formData.toolsAndTechnologies} onChange={(e) => handleInputChange('toolsAndTechnologies', e.target.value)} placeholder="React, AWS, Docker, PostgreSQL" />
              </div>
            </div>

            {/* Motivation & Fit */}
            <div className="cl-form-section">
              <h2 className="cl-section-title">
                <svg viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Motivation & Fit
              </h2>
              
              <div className="cl-form-group">
                <label>Why are you excited about this company?</label>
                <textarea value={formData.companyMotivation} onChange={(e) => handleInputChange('companyMotivation', e.target.value)} rows="3" placeholder="your company's commitment to innovation and cutting-edge technology solutions" />
              </div>

              <div className="cl-form-group">
                <label>Relevant Experience</label>
                <textarea value={formData.relevantExperience} onChange={(e) => handleInputChange('relevantExperience', e.target.value)} rows="2" placeholder="building scalable web applications and leading cross-functional teams" />
              </div>
            </div>

            {/* Mobile Preview Toggle Button */}
            <div className="cl-mobile-preview-toggle">
              <button className="cl-mobile-toggle-btn" onClick={() => setMobileView('preview')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                View Live Preview
              </button>
              <p className="cl-mobile-guide-text">Preview your cover letter in real-time</p>
            </div>

          </div>
        </div>

        {/* Right: Fixed Preview */}
        <div className="cl-editor-preview">
          <div className="cl-preview-header">
            {/* Mobile Back to Editor button */}
            <button className="cl-mobile-back-editor" onClick={() => setMobileView('editor')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Editor
            </button>
            <h2>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              Live Preview
            </h2>
            <div className="cl-preview-controls">
              {/* Zoom controls */}
              <div className="cl-zoom-controls">
                <button className="cl-zoom-btn" onClick={handleClZoomOut} title="Zoom Out">−</button>
                <span className="cl-zoom-label">{Math.round(clPreviewZoom * 100)}%</span>
                <button className="cl-zoom-btn" onClick={handleClZoomIn} title="Zoom In">+</button>
                <button className="cl-zoom-btn cl-zoom-fit" onClick={handleClZoomFit} title="Fit to Width">⊡</button>
              </div>
              <button
                ref={colorBtnRef}
                className={`cl-theme-btn ${!tierColorPolicy.enabled ? 'cl-theme-btn-locked' : ''}`}
                onClick={handleColorPickerToggle}
                title={tierColorPolicy.enabled ? 'Change Color Theme' : tierColorPolicy.label}
              >
                <span>{tierColorPolicy.enabled ? '🎨' : '🔒'}</span>
              </button>
              <button
                ref={fontBtnRef}
                className="cl-theme-btn"
                onClick={handleFontPickerToggle}
                title="Change Font"
              >
                <span>Aa</span>
              </button>
              <button className="cl-download-btn" onClick={handleDownloadPDF} disabled={downloading}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {downloading ? 'Downloading...' : 'Download PDF'}
              </button>
            </div>
          </div>
          <div className="cl-preview-content">
            <div
              className="cl-preview-a4-wrapper"
              style={{
                width: Math.round(794 * clPreviewZoom),
                height: Math.round(clIframeHeight * clPreviewZoom),
                margin: '0 auto',
                flexShrink: 0,
              }}
            >
              <div
                className="cl-preview-zoom-stage"
                style={{
                  width: 794,
                  height: clIframeHeight,
                  transform: `scale(${clPreviewZoom})`,
                  transformOrigin: 'top left',
                }}
              >
                <iframe
                  ref={clIframeRef}
                  srcDoc={previewHTML}
                  title="Cover Letter Preview"
                  className="cl-preview-iframe"
                  scrolling="no"
                  sandbox="allow-scripts"
                  style={{ height: clIframeHeight }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Theme Dropdowns */}
      {showColorPicker && (
        <div
          className="cl-theme-dropdown"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <div className="cl-dropdown-header">
            <span className="cl-dropdown-title">🎨 Color Themes</span>
            <button className="cl-dropdown-close" onClick={() => setShowColorPicker(false)}>✕</button>
          </div>
          {!tierColorPolicy.enabled ? (
            <div style={{ padding: '16px 20px', color: '#64748b', fontSize: '13px', textAlign: 'center', lineHeight: '1.5' }}>
              {tierColorPolicy.label}
            </div>
          ) : (
            <div className="cl-color-grid">
              {Object.keys(colorPalettes).map((colorName) => {
                const palette = colorPalettes[colorName];
                return (
                  <button
                    key={colorName}
                    className={`cl-color-swatch ${colorTheme === colorName ? 'active' : ''}`}
                    onClick={() => handleColorChange(colorName)}
                    title={colorName.charAt(0).toUpperCase() + colorName.slice(1)}
                    style={{ background: `linear-gradient(135deg, ${palette.gradient[0]}, ${palette.gradient[1]})` }}
                  >
                    {colorTheme === colorName && <span className="cl-check-icon">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showFontPicker && (
        <div
          className="cl-theme-dropdown"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <div className="cl-dropdown-header">
            <span className="cl-dropdown-title">🔤 Font Families</span>
            <button className="cl-dropdown-close" onClick={() => setShowFontPicker(false)}>✕</button>
          </div>
          <div
            className="cl-font-list"
            ref={fontListRef}
            onKeyDown={handleFontKeyDown}
          >
            {fontFamilies.map((font, idx) => (
              <button
                key={font.value}
                className={`cl-font-option ${fontFamily === font.value ? 'active' : ''}`}
                onClick={() => handleFontChange(font.value)}
                onFocus={() => setFontFocusIdx(idx)}
                style={{ fontFamily: `'${font.value}', sans-serif` }}
                tabIndex={0}
              >
                <span className="cl-font-name">{font.label}</span>
                {fontFamily === font.value && <span className="cl-check-icon">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Export Progress Dialog */}
      {showExportDialog && (
        <div className="cl-export-overlay">
          <div className="cl-export-dialog">
            <div className="cl-export-spinner"></div>
            <p className="cl-export-status">{exportStatus}</p>
            <div className="cl-export-progress-bar">
              <div className="cl-export-progress-fill" style={{ width: `${exportProgress}%` }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal 
        show={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onCropComplete={handlePhotoUpload}
      />


    </div>
  );
};

export default CoverLetterEditor;
